import React, { useState, useEffect } from 'react';
import { Table, Typography, Space, message, Button } from 'antd';
import { ReloadOutlined, DownloadOutlined, ClearOutlined } from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import { useSearchParams, useNavigate } from 'react-router';
import apiClient from '../../api.js';
import FileAnalysisModal from '../Modal/Modal.jsx';
import './AnalysisTable.css';

const { Title } = Typography;

const AnalysisTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get filepath filter from query parameters
  const filepathFilter = searchParams.get('filepath');
  
  // Function to clear the filepath filter
  const handleClearFilter = () => {
    setSearchParams({});
  };
  
  // Define table columns based on the API response structure
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'File Name',
      dataIndex: 'filename',
      key: 'filename',
      ellipsis: true,
      render: text => <span title={text}>{text}</span>,
      sorter: (a, b) => a.filename.localeCompare(b.filename),
      filterSearch: true,
      onFilter: (value, record) => 
        record.filename.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'File Path',
      dataIndex: 'filepath',
      key: 'filepath',
      ellipsis: true,
      render: text => (
        <span
          title={text}
          style={{ fontFamily: 'monospace', fontSize: '12px' }}
        >
          {text}
        </span>
      ),
      sorter: (a, b) => a.filepath.localeCompare(b.filepath),
      filterSearch: true,
      onFilter: (value, record) => 
        record.filepath.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Progress',
      dataIndex: 'progress',
      key: 'progress',
      width: 100,
      render: progress => {
        const percentage = Math.round(progress || 0);
        let color = '#52c41a'; // Green for completed
        if (percentage < 100) {
          color = percentage > 0 ? '#faad14' : '#d9d9d9'; // Yellow for in progress, gray for not started
        }
        return (
          <span style={{ color }}>
            {percentage}%
          </span>
        );
      },
      sorter: (a, b) => (a.progress || 0) - (b.progress || 0),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: status => {
        let color = '#52c41a'; // Green
        if (status === 'In Queue') {
          color = '#faad14'; // Yellow
        } else if (status === 'Processing') {
          color = '#1890ff'; // Blue
        } else if (status === 'Failed') {
          color = '#ff4d4f'; // Red
        }
        
        return (
          <span style={{ color }}>
            {status || 'Unknown'}
          </span>
        );
      },
      filters: [
        { text: 'Completed', value: 'Completed' },
        { text: 'Processing', value: 'Processing' },
        { text: 'In Queue', value: 'In Queue' },
        { text: 'Failed', value: 'Failed' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Last Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: date => {
        if (!date) return '-';
        const dateObj = new Date(date);
        return (
          <div style={{ fontSize: '12px' }}>
            <div>{dateObj.toLocaleDateString()}</div>
            <div style={{ color: '#8c8c8c' }}>
              {dateObj.toLocaleTimeString()}
            </div>
          </div>
        );
      },
      sorter: (a, b) => {
        if (!a.updated_at && !b.updated_at) return 0;
        if (!a.updated_at) return 1;
        if (!b.updated_at) return -1;
        return new Date(a.updated_at) - new Date(b.updated_at);
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: record => (
        <Button
          type="text"
          size="small"
          onClick={e => {
            e.stopPropagation();
            handleItemClick(record);
          }}
          disabled={record['progress'] != 100 ? true : false}
        >
          View Analysis
        </Button>
      ),
    },
  ];

  const handleRowClick = record => {
    if (record['progress'] != 100) {
      message.error('File is still processing');
    } else {
      navigate(`/analysis/${record.id}`);
    }
  };

  const handleItemClick = record => {
    setSelectedFile(record);
    setModalState(true);
  };

  // Fetch all analysis data from API
  const fetchAnalysisData = async () => {
    setLoading(true);
    try {
      // Build URL with query parameters if filepath filter is present
      let url = '/analysis';
      if (filepathFilter) {
        url += `?filepath=${encodeURIComponent(filepathFilter)}`;
      }
      
      const response = await apiClient.get(url);

      if (response.data && response.data.files) {
        setData(response.data.files);
      }
    } catch (error) {
      console.error('Error fetching analysis data:', error);
      message.error('Failed to fetch analysis data');
      // Set empty data on error
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Refresh data
  const handleRefresh = () => {
    fetchAnalysisData();
  };

  // Generate CSV data
  const getCsvData = () => {
    return data.map(item => ({
      ID: item.id,
      'File Name': item.filename,
      'File Path': item.filepath,
      Progress: `${item.progress}%`,
      Status: item.status,
      'Last Updated': item.updated_at
        ? new Date(item.updated_at).toLocaleString()
        : '-',
    }));
  };

  // Initialize data on component mount
  useEffect(() => {
    fetchAnalysisData();
  }, [filepathFilter]); // Re-fetch when filepath filter changes

  return (
    <div className="analysis-table-container">
      <div className="analysis-table-header">
        <div className="header-left">
          <Title level={3} style={{ margin: 0 }}>
            File Analysis Results
          </Title>
          {filepathFilter && (
            <div style={{ marginTop: '8px' }}>
              <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
                Filtered by path: <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }}>{filepathFilter}</code>
              </Typography.Text>
            </div>
          )}
          <Typography.Text style={{margin: filepathFilter ? '8px 0 0 0' : '0'}} type="secondary">
            Total: {data.length} files
          </Typography.Text>
        </div>

        <div className="header-right">
          <Space>
            <CSVLink
              data={getCsvData()}
              filename={`analysis-results-${
                new Date().toISOString().split('T')[0]
              }.csv`}
              className="csv-download-link"
            >
              <Button icon={<DownloadOutlined />}>Export CSV</Button>
            </CSVLink>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
            {filepathFilter && (
              <Button
                icon={<ClearOutlined />}
                onClick={handleClearFilter}
              >
                Clear Filter
              </Button>
            )}
          </Space>
        </div>
      </div>

      <Table
        className="analysis-table"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onRow={record => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' },
        })}
        scroll={{ x: 1000 }}
        rowKey="id"
        size="middle"
      />
      <FileAnalysisModal
        title="File Analysis Details"
        modalState={modalState}
        setModalState={setModalState}
        selectedFile={selectedFile}
      />
    </div>
  );
};

export default AnalysisTable;
